import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import DirectionsChip from "../../components/DirectionsChip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ViewListIcon from "@mui/icons-material/ViewList";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventIcon from "@mui/icons-material/Event";
import DownloadIcon from "@mui/icons-material/Download";
import SportsHockeyIcon from "@mui/icons-material/SportsHockey";
import { useNavigate } from "react-router-dom";
import { getMyGames } from "../../services/api/games";
import { getCalendarToken, regenerateCalendarToken } from "../../services/auth/profile-api";
import { SERVER_BASE_URL } from "../../services/api/client";
import { hasLocallyValidSession } from "../../services/auth/auth-session";
import LoginDialog from "../auth/login-dialog";
import { formatGameDate, formatGameTime } from "../../components/GameCard";
import { loadErrorMessage } from "../../utils/api-error";

const ROLE_LABELS = { SKATER: "Skater", GOALIE: "Goalie" };

function formatTimeRange(startsAt, durationMinutes) {
  const start = new Date(startsAt);
  const startStr = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  if (!durationMinutes) return startStr;
  const end = new Date(start.getTime() + durationMinutes * 60000);
  const endStr = end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${startStr} – ${endStr}`;
}

function buildCalendarUrls(token) {
  const httpsUrl = `${SERVER_BASE_URL}/calendar/${token}/feed.ics`;
  const webcalUrl = httpsUrl.replace(/^https?:\/\//, "webcal://");
  // Google Calendar's "add by link" address (for the website on a computer). It takes the feed as a webcal:// address.
  const googleUrl = `https://calendar.google.com/calendar/r?cid=${webcalUrl}`;
  // The same feed, saved as a file instead of being read by a calendar app.
  const downloadUrl = `${httpsUrl}?download=1`;
  return { httpsUrl, webcalUrl, googleUrl, downloadUrl };
}

/**
 * Which kind of device this is, because what works to add a calendar differs:
 *  - "desktop": Google Calendar's website can subscribe to a link, so can Apple Calendar and Outlook.
 *  - "ios": the built-in Calendar app subscribes to a webcal:// link.
 *  - "android": the Google Calendar app can't subscribe to a link. It takes the "add by link" address for one of its
 *    own calendar IDs, decodes it into scrambled characters, and adds nothing. A downloaded .ics file does import.
 */
function calendarPlatform(userAgent) {
  if (/android/i.test(userAgent)) return "android";
  if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
  return "desktop";
}

function CalendarSubscribeDialog({ open, onClose }) {
  const [token, setToken] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [regenerating, setRegenerating] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [error, setError] = React.useState("");
  const platform = calendarPlatform(navigator.userAgent);

  React.useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError("");
    getCalendarToken()
      .then((data) => setToken(data.calendarToken))
      .catch((err) => setError(loadErrorMessage(err, "Failed to load calendar link.")))
      .finally(() => setLoading(false));
  }, [open]);

  const handleRegenerate = () => {
    setRegenerating(true);
    setError("");
    regenerateCalendarToken()
      .then((data) => setToken(data.calendarToken))
      .catch(() => setError("Failed to regenerate link."))
      .finally(() => setRegenerating(false));
  };

  const { httpsUrl, webcalUrl, googleUrl, downloadUrl } = token ? buildCalendarUrls(token) : {};

  // The https:// address works everywhere: Google's "From URL" box, Outlook, and Apple's "New Calendar
  // Subscription". It is also what to open in a browser to check the feed.
  const handleCopy = () => {
    if (!httpsUrl) return;
    navigator.clipboard.writeText(httpsUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleAddToGoogle = () => {
    window.open(googleUrl, "_blank", "noopener,noreferrer");
  };

  // Hands the webcal:// address to the device's own calendar app (Apple Calendar, Outlook, ...).
  const handleOpenCalendarApp = () => {
    window.location.href = webcalUrl;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Your Schedule to a Calendar</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Put the games you&apos;re signed up for in Google Calendar, Apple Calendar, Outlook or another calendar app.
            Subscribing keeps it up to date as you sign up for more games.
          </Typography>

          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : token ? (
            <>
              <OutlinedInput
                value={httpsUrl}
                readOnly
                inputProps={{ "aria-label": "Calendar link" }}
                size="small"
                sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                endAdornment={
                  <InputAdornment position="end">
                    <Tooltip title={copied ? "Copied!" : "Copy link"}>
                      <IconButton size="small" onClick={handleCopy} edge="end">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                }
              />

              {platform === "android" ? (
                <Alert severity="info" sx={{ "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
                  <strong>On an Android phone,</strong> the Google Calendar app can&apos;t subscribe to a link. If you tap
                  a link that tries, it shows a scrambled calendar name and adds nothing. To keep your games up to date,
                  add the link above <strong>once on a computer</strong> (steps below). It then shows up on your phone
                  too. Or use the button below to add your games to your phone right now.
                </Alert>
              ) : null}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                {platform === "desktop" ? (
                  <>
                    <Button variant="contained" size="small" onClick={handleAddToGoogle} startIcon={<EventIcon />}>
                      Add to Google Calendar
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleOpenCalendarApp} startIcon={<EventIcon />}>
                      Apple Calendar / Outlook
                    </Button>
                  </>
                ) : null}
                {platform === "ios" ? (
                  <Button variant="contained" size="small" onClick={handleOpenCalendarApp} startIcon={<EventIcon />}>
                    Add to Apple Calendar
                  </Button>
                ) : null}
                <Button
                  variant={platform === "android" ? "contained" : "outlined"}
                  size="small"
                  component="a"
                  href={downloadUrl}
                  startIcon={<DownloadIcon />}
                >
                  Download my games (.ics)
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                The download is a one-time copy of your games today. It doesn&apos;t update on its own.
              </Typography>

              {platform !== "android" ? (
                <Alert severity="info" sx={{ py: 0.5, "& .MuiAlert-message": { fontSize: "0.85rem" } }}>
                  Google Calendar can take a few hours (sometimes up to a day) to show a newly added calendar and to
                  pick up changes. That is on Google&apos;s side, not something you did wrong.
                </Alert>
              ) : null}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {platform === "android" ? "Subscribe in Google Calendar (on a computer)" : "Not showing up in Google Calendar?"}
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 }, color: "text.secondary", fontSize: "0.85rem" }}>
                  <li>
                    Check the link works:{" "}
                    <a href={httpsUrl} target="_blank" rel="noopener noreferrer">
                      open it in a browser
                    </a>
                    . You should see calendar text with your games in it (or your browser may download a file).
                  </li>
                  <li>
                    On a computer, open Google Calendar, click <strong>+</strong> next to <strong>Other calendars</strong>,
                    choose <strong>From URL</strong>, and paste the link above.
                  </li>
                  <li>
                    It then syncs to your phone. In the Google Calendar app, open Settings, pick your account, and make
                    sure this calendar is turned on. It may not appear right away.
                  </li>
                  <li>It only lists games you&apos;ve signed up for, not games you only organize.</li>
                </Box>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ pt: 0.5 }}>
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  startIcon={regenerating ? <CircularProgress size={14} /> : <RefreshIcon />}
                  onClick={handleRegenerate}
                  disabled={regenerating}
                >
                  Regenerate Link
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Invalidates the old link — you&apos;ll need to re-subscribe in your calendar app.
                </Typography>
              </Stack>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function GameCalendar({ allRegistrations }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [calDate, setCalDate] = React.useState(() => new Date());
  const [dayModal, setDayModal] = React.useState({ open: false, label: "", regs: [] });

  const year = calDate.getFullYear();
  const month = calDate.getMonth();

  const gamesByDate = React.useMemo(() => {
    const map = {};
    for (const reg of allRegistrations) {
      const d = new Date(reg.game.startsAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map[key]) map[key] = [];
      map[key].push(reg);
    }
    return map;
  }, [allRegistrations]);

  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isToday = (d) =>
    d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthLabel = calDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const isDark = theme.palette.mode === "dark";

  const pillBg = (role) =>
    role === "GOALIE" ? theme.palette.secondary.main : theme.palette.primary.main;

  const openDayModal = (day, regs) => {
    if (regs.length === 0) return;
    const label = new Date(year, month, day).toLocaleDateString("en-US", {
      weekday: "long", month: "long", day: "numeric",
    });
    setDayModal({ open: true, label, regs });
  };

  return (
    <Box>
      {/* Day detail modal */}
      <Dialog
        open={dayModal.open}
        onClose={() => setDayModal((d) => ({ ...d, open: false }))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>{dayModal.label}</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Stack spacing={1}>
            {dayModal.regs.map((reg) => (
              <Box
                key={reg.registrationId}
                onClick={() => {
                  setDayModal((d) => ({ ...d, open: false }));
                  navigate(`/games/${reg.game.id}/roster`);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: reg.game.status === "CANCELLED" ? "rgba(140, 20, 20, 0.7)" : pillBg(reg.role),
                  color: "#fff",
                  cursor: "pointer",
                  "&:hover": { filter: "brightness(0.88)" },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ color: "#fff" }}>
                    {reg.game.arenaName || reg.game.league?.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)" }}>
                    {formatTimeRange(reg.game.startsAt, reg.game.durationMinutes)}
                    {reg.game.league?.name && reg.game.arenaName ? ` · ${reg.game.league.name}` : ""}
                  </Typography>
                </Box>
                {reg.game.status === "CANCELLED" && (
                  <Chip
                    label="Cancelled"
                    size="small"
                    sx={{ bgcolor: "rgba(0,0,0,0.3)", color: "#fff", fontWeight: 700, flexShrink: 0 }}
                  />
                )}
                <Chip
                  label={reg.role === "GOALIE" ? "Goalie" : "Skater"}
                  size="small"
                  sx={{ bgcolor: "rgba(0,0,0,0.2)", color: "#fff", fontWeight: 600, flexShrink: 0 }}
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDayModal((d) => ({ ...d, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Month navigation */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <IconButton size="small" onClick={() => setCalDate(new Date(year, month - 1, 1))}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={600}>{monthLabel}</Typography>
        <IconButton size="small" onClick={() => setCalDate(new Date(year, month + 1, 1))}>
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      {/* Day-of-week header */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
          <Typography
            key={label}
            variant="caption"
            align="center"
            color="text.secondary"
            fontWeight={600}
            sx={{ py: 0.5 }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
        {cells.map((day, idx) => {
          if (day === null) {
            return (
              <Box
                key={`pad-${idx}`}
                sx={{ minHeight: { xs: 56, sm: 80 }, bgcolor: "transparent" }}
              />
            );
          }

          const key = `${year}-${month}-${day}`;
          const regs = (gamesByDate[key] || []).sort(
            (a, b) => new Date(a.game.startsAt) - new Date(b.game.startsAt)
          );
          const todayCell = isToday(day);

          return (
            <Box
              key={day}
              onClick={() => openDayModal(day, regs)}
              sx={{
                minHeight: { xs: 56, sm: 80 },
                border: "1px solid",
                borderColor: todayCell ? "primary.main" : "divider",
                borderRadius: 1,
                p: "4px",
                bgcolor: isDark
                  ? todayCell ? "rgba(25,118,210,0.12)" : "background.paper"
                  : todayCell ? "primary.50" : "background.paper",
                overflow: "hidden",
                cursor: regs.length > 0 ? "pointer" : "default",
                "&:hover": regs.length > 0 ? { bgcolor: isDark ? "action.hover" : "grey.100" } : {},
              }}
            >
              {/* Day number */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: "2px" }}>
                <Typography
                  variant="caption"
                  sx={{
                    width: 22,
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    fontWeight: todayCell ? 700 : 400,
                    bgcolor: todayCell ? "primary.main" : "transparent",
                    color: todayCell ? "primary.contrastText" : "text.secondary",
                    fontSize: "0.7rem",
                    flexShrink: 0,
                  }}
                >
                  {day}
                </Typography>
              </Box>

              {/* Event pills */}
              <Stack spacing="2px">
                {regs.map((reg) => (
                  <Box
                    key={reg.registrationId}
                    title={`${reg.game.arenaName || reg.game.league?.name} · ${formatTimeRange(reg.game.startsAt, reg.game.durationMinutes)}${reg.game.status === "CANCELLED" ? " · Cancelled" : ""}`}
                    sx={{
                      bgcolor: reg.game.status === "CANCELLED" ? "rgba(160, 20, 20, 0.55)" : pillBg(reg.role),
                      color: "#fff",
                      borderRadius: "3px",
                      px: "3px",
                      py: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                        textDecoration: reg.game.status === "CANCELLED" ? "line-through" : "none",
                      }}
                    >
                      {formatTimeRange(reg.game.startsAt, reg.game.durationMinutes)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "primary.main" }} />
          <Typography variant="caption" color="text.secondary">Skater</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "secondary.main" }} />
          <Typography variant="caption" color="text.secondary">Goalie</Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

export default function MyActivityView() {
  const navigate = useNavigate();
  const [allRegistrations, setAllRegistrations] = React.useState([]);
  const [registrations, setRegistrations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [gamePage, setGamePage] = React.useState(1);
  const [gamePageSize, setGamePageSize] = React.useState(5);
  const [viewMode, setViewMode] = React.useState("calendar");
  const [subscribeOpen, setSubscribeOpen] = React.useState(false);
  // Someone who isn't signed in (for example, arriving from a "View my schedule" button in an email) is asked
  // to sign in right here, and then sees their schedule. They are not sent to the pricing page.
  const [needsSignIn, setNeedsSignIn] = React.useState(false);
  const [loginOpen, setLoginOpen] = React.useState(false);

  const askToSignIn = React.useCallback(() => {
    setNeedsSignIn(true);
    setLoginOpen(true);
    setLoading(false);
  }, []);

  const loadSchedule = React.useCallback(() => {
    if (!hasLocallyValidSession()) {
      askToSignIn();
      return;
    }
    setNeedsSignIn(false);
    setError("");
    setLoading(true);
    getMyGames()
      .then((data) => {
        const all = Array.isArray(data) ? data : [];
        const now = new Date();
        setAllRegistrations(all);
        setRegistrations(all.filter((r) => new Date(r.game.startsAt) >= now));
      })
      .catch((err) => {
        if (err?.message?.includes("Authentication required")) {
          askToSignIn();
          return;
        }
        setError(loadErrorMessage(err, "Something went wrong loading your schedule. Please refresh and try again."));
      })
      .finally(() => setLoading(false));
  }, [askToSignIn]);

  React.useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  if (needsSignIn) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <LoginDialog
          open={loginOpen}
          onClose={() => setLoginOpen(false)}
          onSuccess={() => {
            setLoginOpen(false);
            loadSchedule();
          }}
        />
        <Stack spacing={2} alignItems="center" sx={{ textAlign: "center" }}>
          <Typography variant="h4" component="h3">
            My schedule
          </Typography>
          <Typography color="text.secondary">Sign in to see the games you&apos;re signed up for.</Typography>
          <Button variant="contained" size="large" onClick={() => setLoginOpen(true)}>
            Sign in
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={4}>
        {/* Page header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Typography variant="h4" component="h3">
            My schedule
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<CalendarMonthIcon fontSize="small" />}
              onClick={() => setSubscribeOpen(true)}
            >
              Subscribe
            </Button>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, val) => { if (val) setViewMode(val); }}
              size="small"
            >
              <ToggleButton value="list" aria-label="List view">
                <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
                List
              </ToggleButton>
              <ToggleButton value="calendar" aria-label="Calendar view">
                <CalendarMonthIcon fontSize="small" sx={{ mr: 0.5 }} />
                Calendar
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        <CalendarSubscribeDialog open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />

        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {/* Games section */}
        <Box>
          {loading ? (
            <Typography color="text.secondary">Loading…</Typography>
          ) : viewMode === "calendar" ? (
            <>
              <GameCalendar allRegistrations={allRegistrations} />
              {allRegistrations.length === 0 && (
                <Box textAlign="center" sx={{ pt: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    You&apos;re not signed up for any games yet.
                  </Typography>
                  <Button variant="outlined" size="small" onClick={() => navigate("/search")}>
                    Find a game
                  </Button>
                </Box>
              )}
            </>
          ) : registrations.length === 0 ? (
            <Box textAlign="center" sx={{ py: 8 }}>
              <SportsHockeyIcon sx={{ fontSize: 52, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No upcoming games
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                You&apos;re not signed up for any upcoming games yet.
              </Typography>
              <Button variant="contained" onClick={() => navigate("/search")}>
                Find a game
              </Button>
            </Box>
          ) : (
            <Stack spacing={2}>
              {registrations.slice((gamePage - 1) * gamePageSize, gamePage * gamePageSize).map((reg) => (
                <Card key={reg.registrationId} variant="outlined" sx={{ position: "relative", overflow: "hidden" }}>
                  {reg.game.status === "CANCELLED" && (
                    <Box
                      aria-hidden
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        zIndex: 1,
                      }}
                    >
                      <Typography sx={{
                        fontSize: "2.5rem",
                        fontWeight: 900,
                        color: "rgba(200, 30, 30, 0.42)",
                        transform: "rotate(-15deg)",
                        userSelect: "none",
                        letterSpacing: "0.2em",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}>
                        CANCELLED
                      </Typography>
                    </Box>
                  )}
                  <CardContent>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ sm: "center" }}
                      gap={2}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {reg.game.arenaName}
                          </Typography>
                          <Chip
                            size="small"
                            label={ROLE_LABELS[reg.role] ?? reg.role}
                            color={reg.role === "GOALIE" ? "secondary" : "primary"}
                            variant="outlined"
                          />
                          {(() => {
                            const cost = reg.role === "GOALIE"
                              ? reg.game.league?.goalieCost
                              : reg.game.league?.playerCost;
                            if (!cost) return null;
                            return (
                              <Chip
                                size="small"
                                label={reg.hasPaid ? "Paid" : "Pending payment"}
                                color={reg.hasPaid ? "success" : "warning"}
                                variant="outlined"
                              />
                            );
                          })()}
                          <DirectionsChip address={reg.game.rink?.address} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {formatGameDate(reg.game.startsAt)} at {formatGameTime(reg.game.startsAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {reg.game.league.name}
                          {reg.game.league.city && reg.game.league.state
                            ? ` · ${reg.game.league.city}, ${reg.game.league.state}`
                            : ""}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/games/${reg.game.id}/roster`)}
                        sx={{ flexShrink: 0 }}
                      >
                        View Roster
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ pt: 1 }}>
                <Pagination
                  count={Math.ceil(registrations.length / gamePageSize)}
                  page={gamePage}
                  onChange={(_, p) => setGamePage(p)}
                  size="small"
                  siblingCount={1}
                />
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" color="text.secondary">Per page:</Typography>
                  <Select
                    size="small"
                    value={gamePageSize}
                    onChange={(e) => { setGamePageSize(e.target.value); setGamePage(1); }}
                    sx={{ fontSize: "0.8rem", height: 28 }}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={registrations.length}>All</MenuItem>
                  </Select>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Box>

      </Stack>
    </Container>
  );
}
